/**
 * The line a page reports an outcome on.
 *
 * ALWAYS IN THE TREE, empty when idle - never hidden. That is the whole design, and it is the fix
 * for a bug the OpenSilver app hit twice: a status TextBlock bound to `Visibility` is Collapsed
 * exactly while it has nothing to say, which is the state it is in when a request is SENT, so
 * revealing it later inside an already-collapsed panel left it unarranged and every answer the
 * server sent landed somewhere invisible. A technician saw a confirmation dialog and then silence.
 *
 * Here the label is always present and simply says nothing, so there is no reveal to go wrong.
 */
import { insets, type Size, type StackPanel } from 'webwin';
import { CrmLabel } from './Text.js';
import { FONT, GAP, relayout } from './layout.js';

export type StatusKind = 'info' | 'ok' | 'error' | 'busy';

export interface StatusLineOptions {
  /**
   * Measure no height while empty. Still in the tree and still visible - the rule above holds - so
   * `set` only has to relayout, which it already does. For a header with several lines that are
   * nearly always idle, where a line's height each would leave a band of empty space.
   */
  collapse?: boolean;
}

class CollapsingLabel extends CrmLabel {
  override preferredSize(available: Size): Size {
    return this.text ? super.preferredSize(available) : { width: 0, height: 0 };
  }
}

export class StatusLine {
  readonly label: CrmLabel;

  constructor(options: StatusLineOptions = {}) {
    this.label = new (options.collapse ? CollapsingLabel : CrmLabel)({
      text: '',
      wrap: true,
      autoHeight: true,
      hAlign: 'stretch',
      font: FONT.small,
      role: 'muted',
      margin: insets(0, GAP.xs, 0, 0)
    });
  }

  /** Adds the line to a stack. Returns the stack, so it can close a `stackOf` chain. */
  addTo(stack: StackPanel): StackPanel {
    stack.controls.add(this.label);
    return stack;
  }

  set(message: string, kind: StatusKind = 'info'): void {
    const changed = message !== this.label.text;
    this.label.text = message;
    this.label.role = kind === 'error' ? 'danger' : kind === 'ok' ? 'ok' : 'muted';
    // A message that wraps onto more lines than the last one makes the line taller, and nothing
    // above it hears about that by itself: a server's two-line refusal was drawn cut off after the
    // first line, inside a card still sized for an empty status.
    if (changed) relayout(this.label);
  }

  /** What a page shows while a request is out. Separate so the wording stays consistent. */
  busy(message: string): void {
    this.set(message, 'busy');
  }

  ok(message: string): void {
    this.set(message, 'ok');
  }

  error(message: string): void {
    this.set(message, 'error');
  }

  clear(): void {
    this.set('');
  }

  get text(): string {
    return this.label.text;
  }
}
